import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, Paper, IconButton, CircularProgress, Fade, TextField, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Refresh as RefreshIcon,
    FileDownload as DownloadIcon,
    TrendingUp as TrendingIcon,
    AccountTree as BranchIcon,
    Business as DeptIcon
} from '@mui/icons-material';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as ChartTooltip, ResponsiveContainer, LabelList, Cell,
    PieChart, Pie
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import AppHeader from '../../components/AppHeader';

const COLORS = [
    '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4',
    '#ec4899', '#f97316', '#3b82f6', '#84cc16', '#a78bfa',
    '#fb923c', '#34d399', '#60a5fa', '#f472b6', '#fbbf24',
    '#2dd4bf', '#818cf8', '#e879f9', '#4ade80', '#facc15'
];

const LateAnalytics = ({ setIsLoggedIn }) => {
    const navigate = useNavigate();
    const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [groupBy, setGroupBy] = useState('department'); // 'department' or 'branch'

    // Default to last 30 days
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    const [dateFrom, setDateFrom] = useState(start.toISOString().split('T')[0]);
    const [dateTo, setDateTo] = useState(end.toISOString().split('T')[0]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/web-reports/late-analytics`, {
                headers: { 'X-Tenant': tenant },
                withCredentials: true,
                params: { date_from: dateFrom, date_to: dateTo, group_by: groupBy }
            });

            if (response.data.success) {
                const processed = response.data.data.map(d => ({
                    ...d,
                    late_percent: Number(d.late_percent)
                }));
                setData(processed);
            }
        } catch (error) {
            console.error('Error fetching late analytics:', error);
            if (error.response?.status === 401) {
                localStorage.removeItem('token');
                setIsLoggedIn(false);
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    }, [navigate, setIsLoggedIn, dateFrom, dateTo, tenant, groupBy]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleExportExcel = () => {
        const exportData = data.map(item => ({
            [groupBy === 'branch' ? 'Branch' : 'Department']: item.name,
            'Total Staff': item.total_staff,
            'Late Staff': item.late_staff,
            'Late Percentage (%)': item.late_percent
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Late Analytics");
        XLSX.writeFile(wb, `Late_Analytics_${groupBy}_${dateFrom}_to_${dateTo}.xlsx`);
    };

    const colLabel = groupBy === 'branch' ? 'Branch' : 'Department';
    const chartTitle = groupBy === 'branch' ? 'Late Staff % by Branch' : 'Late Staff % by Department';
    const pieTitle = groupBy === 'branch' ? 'Late Contribution by Branch' : 'Late Contribution by Department';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f8fafc' }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />
            <Box sx={{ flexGrow: 1, p: { xs: 1.5, md: 2 }, overflowY: 'auto' }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton onClick={() => navigate('/')} sx={{ bgcolor: 'white', border: '1px solid #e2e8f0', '&:hover': { bgcolor: '#f1f5f9' } }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography variant="h5" fontWeight="800" sx={{ color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TrendingIcon sx={{ color: '#ef4444' }} /> Late Analytics
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                                Late Staff Percentage by {colLabel}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        {/* Group By Toggle */}
                        <ToggleButtonGroup
                            value={groupBy}
                            exclusive
                            onChange={(e, val) => { if (val) setGroupBy(val); }}
                            size="small"
                            sx={{ bgcolor: 'white', border: '1px solid #e2e8f0', borderRadius: 2 }}
                        >
                            <ToggleButton value="department" sx={{ gap: 0.5, fontSize: 13, textTransform: 'none', px: 2 }}>
                                <DeptIcon fontSize="small" /> Department
                            </ToggleButton>
                            <ToggleButton value="branch" sx={{ gap: 0.5, fontSize: 13, textTransform: 'none', px: 2 }}>
                                <BranchIcon fontSize="small" /> Branch
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <TextField
                            label="From"
                            type="date"
                            size="small"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ bgcolor: 'white', borderRadius: 1 }}
                        />
                        <TextField
                            label="To"
                            type="date"
                            size="small"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ bgcolor: 'white', borderRadius: 1 }}
                        />
                        <IconButton onClick={fetchData} sx={{ bgcolor: 'white', border: '1px solid #e2e8f0' }}>
                            <RefreshIcon />
                        </IconButton>
                        <IconButton onClick={handleExportExcel} sx={{ bgcolor: 'white', border: '1px solid #e2e8f0', color: '#10b981' }}>
                            <DownloadIcon />
                        </IconButton>
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Fade in={!loading}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    md: 'minmax(380px, 1.2fr) minmax(320px, 0.95fr) minmax(320px, 0.95fr)',
                                },
                                gap: 2,
                                alignItems: 'stretch',
                            }}
                        >
                            {/* 1. Bar Chart */}
                            <Box sx={{ minWidth: 0 }}>
                                <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                                    <Typography variant="h6" fontWeight="700" sx={{ mb: 3, color: '#0f172a' }}>
                                        {chartTitle}
                                    </Typography>
                                    <Box sx={{ width: '100%', pb: 1 }}>
                                        <Box sx={{ height: 500, width: '100%' }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={data} margin={{ top: 30, right: 40, left: 20, bottom: 120 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                                    <XAxis
                                                        dataKey="name"
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tick={{ fill: '#64748b', fontSize: 12 }}
                                                        angle={-45}
                                                        textAnchor="end"
                                                    />
                                                    <YAxis
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tick={{ fill: '#64748b' }}
                                                        unit="%"
                                                    />
                                                    <ChartTooltip
                                                        cursor={{ fill: '#f1f5f9' }}
                                                        contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                                                        formatter={(value, name) => [
                                                            name === 'late_percent' ? `${value}%` : value,
                                                            name === 'late_percent' ? 'Late %' : name
                                                        ]}
                                                    />
                                                    <Bar dataKey="late_percent" radius={[6, 6, 0, 0]} maxBarSize={90}>
                                                        {data.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                        ))}
                                                        <LabelList dataKey="late_percent" position="top" fill="#64748b" fontSize={12} formatter={(val) => `${val}%`} />
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Box>

                            {/* 2. Detailed Breakdown */}
                            <Box sx={{ minWidth: 0 }}>
                                <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid #e2e8f0', height: '100%' }}>
                                    <Typography variant="h6" fontWeight="700" sx={{ mb: 3, color: '#0f172a' }}>
                                        Detailed Breakdown
                                    </Typography>
                                    <Box sx={{ overflowX: 'auto', maxHeight: 540, overflowY: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                            <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                                                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                                                    <th style={{ padding: '12px', color: '#64748b', fontWeight: 600 }}>{colLabel}</th>
                                                    <th style={{ padding: '12px', color: '#64748b', fontWeight: 600 }}>Total Staff</th>
                                                    <th style={{ padding: '12px', color: '#64748b', fontWeight: 600 }}>Late Staff</th>
                                                    <th style={{ padding: '12px', color: '#64748b', fontWeight: 600 }}>Late %</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.map((row, idx) => (
                                                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                        <td style={{ padding: '12px', fontWeight: 500, color: '#0f172a' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS[idx % COLORS.length], flexShrink: 0 }} />
                                                                {row.name}
                                                            </Box>
                                                        </td>
                                                        <td style={{ padding: '12px', color: '#475569' }}>{row.total_staff}</td>
                                                        <td style={{ padding: '12px', color: '#ef4444', fontWeight: 600 }}>{row.late_staff}</td>
                                                        <td style={{ padding: '12px', color: '#0f172a', fontWeight: 600 }}>{row.late_percent}%</td>
                                                    </tr>
                                                ))}
                                                {data.length === 0 && (
                                                    <tr>
                                                        <td colSpan={4} style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                                                            No late staff recorded for this period.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </Box>
                                </Paper>
                            </Box>

                            {/* 3. Pie Chart */}
                            <Box sx={{ minWidth: 0 }}>
                                <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid #e2e8f0', height: '100%' }}>
                                    <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#0f172a' }}>
                                        {pieTitle}
                                    </Typography>
                                    <Box sx={{ height: 360, width: '100%' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={data.filter(d => d.late_staff > 0)}
                                                    cx="50%"
                                                    cy="50%"
                                                    labelLine={false}
                                                    label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                                                        if (percent < 0.04) return null;
                                                        const RADIAN = Math.PI / 180;
                                                        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                                                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                                        const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                                        return (
                                                            <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={13} fontWeight="bold">
                                                                {`${(percent * 100).toFixed(0)}%`}
                                                            </text>
                                                        );
                                                    }}
                                                    outerRadius="78%"
                                                    fill="#8884d8"
                                                    dataKey="late_staff"
                                                >
                                                    {data.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <ChartTooltip
                                                    contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                                                    formatter={(value, name) => [`${value} Staff`, name]}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </Paper>
                            </Box>
                        </Box>
                    </Fade>
                )}
            </Box>
        </Box>
    );
};

export default LateAnalytics;
